/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
   FaRegCalendarAlt,
   FaMapMarkerAlt,
   FaChevronLeft,
   FaChevronRight,
   FaPlus,
   FaRoute,
   FaUsers,
   FaReceipt,
} from 'react-icons/fa';

import { searchRoutingSchedules } from '../../../../../services/routing/routingScheduleApi';
import {
   CatalogCard,
   CatHead,
   BaseSeg,
   CatTrack,
   CatRail,
   CatCol,
   CatCell,
   CatCellEmpty,
   CatAdd,
   CatPop,
} from './styles';
import {
   SCHEDULE_PERIOD_MORNING,
   SCHEDULE_PERIOD_AFTERNOON,
   todayIsoLocal,
   addDaysIso,
   formatWeekdayShortBr,
   formatDayMonthBr,
   formatBrlCurrency,
} from './importHelpers';

// derived_status → rótulo + cor/tint. ESPELHA ScheduleStatusBadge (fonte da
// verdade do vocabulário de status do módulo — handoff 0060 §1.2: sem "publicada").
const CATALOG_STATUS = {
   pending:  { label: 'Rascunho',      color: '#7d5a00', tint: 'rgba(242,175,41,0.15)' },
   special:  { label: 'Em preparação', color: '#3730a3', tint: 'rgba(99,102,241,0.12)' },
   progress: { label: 'Em execução',   color: '#0C447C', tint: 'rgba(55,138,221,0.12)' },
   done:     { label: 'Concluída',     color: '#1b5e20', tint: 'rgba(46,125,50,0.12)' },
};

const PERIODS = [
   { key: SCHEDULE_PERIOD_MORNING, label: 'Manhã' },
   { key: SCHEDULE_PERIOD_AFTERNOON, label: 'Tarde' },
];

const COL_OFFSETS = [-2, -1, 0, 1, 2];

// ===========================================================================
// RouteCatalog — faixa informativa (Slice 2) de 5 dias × 2 períodos. Reusa
// POST /routing/schedules/search (service inalterado); ausência de linha p/ uma
// (data, período) = célula VAZIA. O + de cada célula dispara o upload daquela
// data/período/base (onImport). Sem ação no sistema além do gatilho de import.
// ===========================================================================
export default function RouteCatalog({
   clusterOrigin = 'GOIANIA',
   clusterOrigins = [],
   onImport,
}) {
   const today = useMemo(() => todayIsoLocal(), []);
   const [focus, setFocus] = useState(0); // offset (dias) da coluna central
   const [base, setBase] = useState(clusterOrigin);
   const [schedulesByKey, setSchedulesByKey] = useState(() => new Map());

   const baseOptions =
      clusterOrigins.length > 0
         ? clusterOrigins
         : [{ origin_code: base, origin_label: base }];

   const columns = useMemo(
      () => COL_OFFSETS.map((i) => focus + i),
      [focus],
   );

   // Fetch da janela visível ao mudar base/foco. A faixa é pequena (5 datas ×
   // 2 períodos) — limit alto p/ não paginar.
   useEffect(() => {
      let cancelled = false;
      const dateFrom = addDaysIso(today, focus - 2);
      const dateTo = addDaysIso(today, focus + 2);

      searchRoutingSchedules({
         clusterOrigin: base,
         dateFrom,
         dateTo,
         perPage: 100,
         sortBy: 'schedule_date',
         sortDirection: 'asc',
      })
         .then(({ schedules }) => {
            if (cancelled) return;
            const map = new Map();
            (schedules || []).forEach((s) => {
               map.set(`${s.schedule_date}|${s.schedule_period}`, s);
            });
            setSchedulesByKey(map);
         })
         .catch(() => {
            // Faixa é informativa — falha silenciosa vira "tudo vazio" (o
            // operador ainda importa pelo +). Sem toast por mudança de foco.
            if (!cancelled) setSchedulesByKey(new Map());
         });

      return () => {
         cancelled = true;
      };
   }, [base, focus, today]);

   const edgeForIndex = (index) => {
      if (index <= 1) return 'left';
      if (index >= 3) return 'right';
      return null;
   };

   return (
      <CatalogCard>
         <CatHead>
            <span className="cat-ic">
               <FaRegCalendarAlt size={17} />
            </span>
            <div className="tt">
               <div className="t">Catálogo de roteirizações</div>
               <div className="d">
                  Passe o mouse sobre um período e clique em <b>+</b> para
                  importar uma planilha
               </div>
            </div>

            <div
               style={{
                  marginLeft: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 12,
               }}
            >
               <BaseSeg role="group" aria-label="Base operacional">
                  {baseOptions.map((b) => (
                     <button
                        key={b.origin_code}
                        type="button"
                        className={base === b.origin_code ? 'active' : ''}
                        onClick={() => setBase(b.origin_code)}
                     >
                        <FaMapMarkerAlt size={11} />{' '}
                        {b.origin_label || b.origin_code}
                     </button>
                  ))}
               </BaseSeg>
               <div className="cat-nav">
                  <button
                     type="button"
                     title="Dias anteriores"
                     onClick={() => setFocus((f) => f - 1)}
                  >
                     <FaChevronLeft size={15} />
                  </button>
                  <button
                     type="button"
                     title="Próximos dias"
                     onClick={() => setFocus((f) => f + 1)}
                  >
                     <FaChevronRight size={15} />
                  </button>
               </div>
            </div>
         </CatHead>

         <CatTrack>
            <CatRail>
               <div className="corner" />
               {PERIODS.map((p) => (
                  <div key={p.key} className="plabel">
                     {p.label}
                  </div>
               ))}
            </CatRail>

            {columns.map((off, index) => {
               const iso = addDaysIso(today, off);
               const isCenter = off === focus;
               const isToday = off === 0;
               const edge = edgeForIndex(index);

               return (
                  <CatCol key={off} $center={isCenter}>
                     <div className="colhead">
                        {isToday ? (
                           <span className="today-tag">Hoje</span>
                        ) : (
                           <span className="wd">{formatWeekdayShortBr(iso)}</span>
                        )}
                        <span className="dt">{formatDayMonthBr(iso)}</span>
                     </div>

                     {PERIODS.map((p) => {
                        const schedule = schedulesByKey.get(`${iso}|${p.key}`);
                        const addButton = (
                           <CatAdd
                              type="button"
                              title={`Importar planilha — ${formatWeekdayShortBr(
                                 iso,
                              )} ${formatDayMonthBr(iso)} · ${p.label}`}
                              onClick={(e) => {
                                 e.stopPropagation();
                                 onImport({
                                    scheduleDate: iso,
                                    schedulePeriod: p.key,
                                    clusterOrigin: base,
                                 });
                              }}
                           >
                              <FaPlus size={13} />
                           </CatAdd>
                        );

                        if (!schedule) {
                           return (
                              <CatCellEmpty key={p.key}>
                                 {addButton}
                                 <span className="cell-empty">
                                    Sem roteirização
                                 </span>
                              </CatCellEmpty>
                           );
                        }

                        const status =
                           CATALOG_STATUS[schedule.derived_status] ?? {
                              label: schedule.derived_status ?? '—',
                              color: '#5b626d',
                              tint: '#eef1f5',
                           };
                        const items = schedule.total_items ?? 0;

                        return (
                           <CatCell
                              key={p.key}
                              $center={isCenter}
                              $edge={edge}
                              style={{ '--c': status.color, '--cbg': status.tint }}
                           >
                              {addButton}
                              <div className="cell-top">
                                 <span className="cell-dot" />
                                 <span className="cell-count">{items}</span>
                                 <span className="cell-unit">itens</span>
                              </div>
                              <div className="cell-status">{status.label}</div>

                              <CatPop className="cat-pop" $edge={edge}>
                                 <div className="pop-h">
                                    <span className="pop-date">
                                       {formatWeekdayShortBr(iso)}{' '}
                                       {formatDayMonthBr(iso)} · {p.label}
                                    </span>
                                    <span className="pop-st">{status.label}</span>
                                 </div>
                                 <div className="pop-grid">
                                    <div className="pop-m">
                                       <span className="k">
                                          <FaRoute size={11} /> Rotas
                                       </span>
                                       <span className="v">
                                          {schedule.routes_count ?? 0}
                                       </span>
                                    </div>
                                    <div className="pop-m">
                                       <span className="k">
                                          <FaUsers size={11} /> Técnicos
                                       </span>
                                       <span className="v">
                                          {schedule.technicians_count ?? 0}
                                       </span>
                                    </div>
                                    <div className="pop-m">
                                       <span className="k">
                                          <FaReceipt size={11} /> Itens
                                       </span>
                                       <span className="v">{items}</span>
                                    </div>
                                    <div className="pop-m">
                                       <span className="k">
                                          <FaMapMarkerAlt size={11} /> Não rot.
                                       </span>
                                       <span className="v">
                                          {schedule.total_unrouted ?? 0}
                                       </span>
                                    </div>
                                    <div className="pop-m full">
                                       <span className="k">Valor total</span>
                                       <span className="v">
                                          {formatBrlCurrency(
                                             schedule.total_order_value,
                                          )}
                                       </span>
                                    </div>
                                 </div>
                              </CatPop>
                           </CatCell>
                        );
                     })}
                  </CatCol>
               );
            })}
         </CatTrack>
      </CatalogCard>
   );
}

RouteCatalog.propTypes = {
   clusterOrigin: PropTypes.string,
   clusterOrigins: PropTypes.arrayOf(
      PropTypes.shape({
         origin_code: PropTypes.string,
         origin_label: PropTypes.string,
      }),
   ),
   onImport: PropTypes.func.isRequired,
};
